/**
 * Dragon Shield Cart Error Interceptor & Toast Manager (L0 Theme Layer)
 *
 * Intercepts Shopify AJAX Cart API calls (/cart/add.js, /cart/change.js, /cart/update.js)
 * and catches HTTP 422 Unprocessable Entity responses generated by Shopify Cart Validation Functions.
 *
 */
(function () {
  'use strict';

  let toastDismissTimer = null;

  // Inject Dragon Shield Branded Toast Styles
  const bannerStyles = `
    .ds-cart-error-banner {
      background-color: #fdf2f4;
      border: 1px solid #d50430;
      color: #252f4f;
      padding: 12px 16px;
      border-radius: 4px;
      margin: 12px 0;
      font-size: 14px;
      line-height: 1.45;
      font-family: inherit;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      box-shadow: 0 4px 14px rgba(37, 47, 79, 0.12);
      animation: dsSlideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      position: relative;
      z-index: 99;
    }
    .ds-cart-error-banner.is-dismissing {
      animation: dsSlideUpFadeOut 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
    .ds-cart-error-banner__body {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      flex: 1;
    }
    .ds-cart-error-banner svg {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      fill: #d50430;
      margin-top: 2px;
    }
    .ds-cart-error-banner__content {
      flex: 1;
      font-weight: 500;
    }
    .ds-cart-error-banner__close {
      background: none;
      border: none;
      color: #252f4f;
      opacity: 0.6;
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
      padding: 4px;
      margin: -4px;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.2s ease, background-color 0.2s ease;
    }
    .ds-cart-error-banner__close:hover {
      opacity: 1;
      background-color: rgba(213, 4, 48, 0.08);
    }
    @keyframes dsSlideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes dsSlideUpFadeOut {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(-10px);
      }
    }
  `;

  function injectStyles() {
    if (!document.getElementById('ds-cart-error-styles')) {
      const style = document.createElement('style');
      style.id = 'ds-cart-error-styles';
      style.textContent = bannerStyles;
      document.head.appendChild(style);
    }
  }

  function createWarningIcon() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = `<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>`;
    return svg;
  }

  function dismissBanner(banner) {
    if (!banner || banner.classList.contains('is-dismissing')) return;
    banner.classList.add('is-dismissing');
    setTimeout(function () {
      if (banner && banner.parentNode) {
        banner.parentNode.removeChild(banner);
      }
    }, 350);
  }

  function renderErrorBanner(message, container) {
    if (!container) return;

    // 1. Clear any existing interceptor banners
    const existing = container.querySelectorAll('.ds-cart-error-banner');
    existing.forEach(function (el) { el.remove(); });

    // 2. Deduplication: Clear duplicate raw error text in .js-product-form-error
    const duplicateErrorTarget = container.querySelector('.js-product-form-error') || document.querySelector('.js-product-form-error');
    if (duplicateErrorTarget) {
      duplicateErrorTarget.innerHTML = '';
    }

    if (toastDismissTimer) {
      clearTimeout(toastDismissTimer);
      toastDismissTimer = null;
    }

    const banner = document.createElement('div');
    banner.className = 'ds-cart-error-banner';
    banner.setAttribute('role', 'alert');
    banner.setAttribute('aria-live', 'polite');

    const body = document.createElement('div');
    body.className = 'ds-cart-error-banner__body';

    const icon = createWarningIcon();
    const content = document.createElement('div');
    content.className = 'ds-cart-error-banner__content';
    content.textContent = message;

    body.appendChild(icon);
    body.appendChild(content);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'ds-cart-error-banner__close';
    closeBtn.setAttribute('aria-label', 'Close error notification');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', function () {
      if (toastDismissTimer) {
        clearTimeout(toastDismissTimer);
        toastDismissTimer = null;
      }
      dismissBanner(banner);
    });

    banner.appendChild(body);
    banner.appendChild(closeBtn);

    container.insertBefore(banner, container.firstChild);

    // Scroll into view if out of viewport
    banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Auto-dismiss toast after 5 seconds (5000ms)
    toastDismissTimer = setTimeout(function () {
      dismissBanner(banner);
    }, 5000);
  }

  function getTargetContainer() {
    return (
      document.querySelector('cart-drawer') ||
      document.querySelector('#cart-drawer') ||
      document.querySelector('.cart-drawer') ||
      document.querySelector('.drawer__inner') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('cart-items') ||
      document.body
    );
  }

  async function handleErrorResponse(response) {
    if (response.status !== 422) {
      return;
    }

    try {
      const data = await response.clone().json();
      const errorMessage = data.description || data.message || 'Validation error: Action cannot be completed.';
      injectStyles();
      renderErrorBanner(errorMessage, getTargetContainer());
    } catch (err) {
      console.warn('[CartErrorInterceptor] Failed to parse 422 response JSON:', err);
      injectStyles();
      renderErrorBanner('Validation error: Action cannot be completed.', getTargetContainer());
    }
  }

  // Intercept fetch API
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';

    if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update')) {
      if (!response.ok) {
        handleErrorResponse(response);
      }
    }
    return response;
  };

  // Intercept XMLHttpRequest
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._url = url;
    return originalXhrOpen.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    this.addEventListener('load', function () {
      if (this._url && (this._url.includes('/cart/add') || this._url.includes('/cart/change') || this._url.includes('/cart/update'))) {
        if (this.status !== 422) {
          return;
        }

        try {
          const data = JSON.parse(this.responseText);
          const errorMessage = data.description || data.message || 'Validation error: Action cannot be completed.';
          injectStyles();
          renderErrorBanner(errorMessage, getTargetContainer());
        } catch (err) {
          console.warn('[CartErrorInterceptor] Failed to parse 422 XHR response JSON:', err);
          injectStyles();
          renderErrorBanner('Validation error: Action cannot be completed.', getTargetContainer());
        }
      }
    });
    return originalXhrSend.apply(this, args);
  };
})();
